/**
 * Prayer Request Button Component
 *
 * A subtle button that opens a modal for submitting prayer requests.
 * Used across all four customer portals: Bridge Club, Store Club, Wholesale, Retail Partner.
 */

import React, { useState } from 'react';
import { GospelPresentation } from './GospelPresentation';
import './PrayerRequestButton.css';

interface PrayerRequestButtonProps {
  portalSource: 'bridge_club' | 'store_club' | 'wholesale' | 'retail_partner';
  userName?: string;
  userEmail?: string;
  submitEndpoint?: string; // Override default endpoint
  onSubmit?: (data: PrayerRequestData) => Promise<void>; // Custom submit handler
}

interface PrayerRequestData {
  request_text: string;
  anonymous: boolean;
  include_name: boolean;
  allow_follow_up: boolean;
  customer_name?: string;
  customer_email?: string;
}

export const PrayerRequestButton: React.FC<PrayerRequestButtonProps> = ({
  portalSource,
  userName,
  userEmail,
  submitEndpoint,
  onSubmit
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showGospel, setShowGospel] = useState(false);
  const [requestText, setRequestText] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [allowFollowUp, setAllowFollowUp] = useState(false);
  const [customName, setCustomName] = useState(userName || '');
  const [customEmail, setCustomEmail] = useState(userEmail || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState('');

  const handleOpen = () => {
    setIsModalOpen(true);
    setShowGospel(false);
    setShowConfirmation(false);
    setError('');
  };

  const handleClose = () => {
    setIsModalOpen(false);
    // Reset form after close animation
    setTimeout(() => {
      setRequestText('');
      setIsAnonymous(true);
      setAllowFollowUp(false);
      setCustomName(userName || '');
      setCustomEmail(userEmail || '');
      setShowGospel(false);
      setShowConfirmation(false);
      setError('');
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!requestText.trim()) {
      setError('Please share your prayer request.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const data: PrayerRequestData = {
      request_text: requestText.trim(),
      anonymous: isAnonymous,
      include_name: !isAnonymous,
      allow_follow_up: allowFollowUp && !isAnonymous,
      customer_name: !isAnonymous ? customName : undefined,
      customer_email: allowFollowUp && !isAnonymous ? customEmail : undefined,
    };

    try {
      if (onSubmit) {
        await onSubmit(data);
      } else {
        // Default API call
        const endpoint = submitEndpoint || '/prayer-requests/';
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          throw new Error('Failed to submit prayer request');
        }
      }

      setShowConfirmation(true);
    } catch (err) {
      console.error('Prayer request submission error:', err);
      setError('We apologize, but something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showGospel) {
    return (
      <GospelPresentation
        isOpen={isModalOpen}
        onClose={handleClose}
        onBack={() => setShowGospel(false)}
        userName={customName || userName}
        userEmail={customEmail || userEmail}
        portalSource={portalSource}
      />
    );
  }

  return (
    <>
      {/* Prayer Request Button - Footer style */}
      <div className="prayer-request-container">
        <button className="prayer-request-button" onClick={handleOpen}>
          <span className="prayer-cross-icon">✝</span>
          <span className="prayer-button-text">Prayer Requests</span>
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="prayer-modal-overlay" onClick={handleClose}>
          <div className="prayer-modal" onClick={(e) => e.stopPropagation()}>
            <button className="prayer-modal-close" onClick={handleClose}>
              &times;
            </button>

            {showConfirmation ? (
              // Confirmation View
              <div className="prayer-confirmation">
                <div className="prayer-confirmation-icon">
                  <span className="prayer-cross-large">✝</span>
                </div>
                <h2>Thank You</h2>
                <p>
                  Thank you for sharing with us. We are honored to pray for you.
                </p>
                <p className="prayer-confirmation-verse">
                  "The prayer of a righteous person is powerful and effective."
                  <br />
                  <em>— James 5:16</em>
                </p>
                <button className="prayer-btn prayer-btn-primary" onClick={handleClose}>
                  Close
                </button>
              </div>
            ) : (
              // Request Form
              <>
                <div className="prayer-modal-header">
                  <span className="prayer-cross-icon-large">✝</span>
                  <h2>Prayer Requests</h2>
                </div>

                <div className="prayer-modal-intro">
                  <p>
                    We believe in the power of prayer and the comfort and strength found
                    in Jesus Christ. If you're going through a hard time or need someone
                    to stand with you in prayer, we would be honored to pray for you.
                  </p>
                </div>

                {error && (
                  <div className="prayer-error">{error}</div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="prayer-form-group">
                    <textarea
                      value={requestText}
                      onChange={(e) => setRequestText(e.target.value)}
                      placeholder="Share your prayer request..."
                      rows={5}
                      className="prayer-textarea"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="prayer-form-group">
                    <div className="prayer-radio-group">
                      <label className="prayer-radio-label">
                        <input
                          type="radio"
                          name="anonymity"
                          checked={isAnonymous}
                          onChange={() => {
                            setIsAnonymous(true);
                            setAllowFollowUp(false);
                          }}
                          disabled={isSubmitting}
                        />
                        <span className="prayer-radio-text">Submit anonymously</span>
                      </label>
                      <label className="prayer-radio-label">
                        <input
                          type="radio"
                          name="anonymity"
                          checked={!isAnonymous}
                          onChange={() => setIsAnonymous(false)}
                          disabled={isSubmitting}
                        />
                        <span className="prayer-radio-text">Include my name</span>
                      </label>
                    </div>
                  </div>

                  {!isAnonymous && (
                    <div className="prayer-name-section">
                      <div className="prayer-form-group">
                        <label className="prayer-label">Your name</label>
                        <input
                          type="text"
                          value={customName}
                          onChange={(e) => setCustomName(e.target.value)}
                          placeholder="Your name"
                          className="prayer-input"
                          disabled={isSubmitting}
                        />
                      </div>

                      <div className="prayer-form-group">
                        <label className="prayer-checkbox-label">
                          <input
                            type="checkbox"
                            checked={allowFollowUp}
                            onChange={(e) => setAllowFollowUp(e.target.checked)}
                            disabled={isSubmitting}
                          />
                          <span className="prayer-checkbox-text">
                            You may follow up with me
                          </span>
                        </label>
                      </div>

                      {allowFollowUp && (
                        <div className="prayer-form-group">
                          <label className="prayer-label">Email address</label>
                          <input
                            type="email"
                            value={customEmail}
                            onChange={(e) => setCustomEmail(e.target.value)}
                            placeholder="your@email.com"
                            className="prayer-input"
                            disabled={isSubmitting}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="prayer-btn prayer-btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </form>

                {/* Gospel Link */}
                <div className="prayer-gospel-link">
                  <div className="prayer-divider" />
                  <p>Curious about the hope we have in Christ?</p>
                  <button
                    className="prayer-btn prayer-btn-link"
                    onClick={() => setShowGospel(true)}
                  >
                    Learn More &rarr;
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PrayerRequestButton;
